import React, { Suspense } from 'react';
import { Typography, Row, Col, Card, Button, Divider, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { getPulseArticleBySlug } from '../content/pulse';
import ArticleCta from './ArticleCta';
import './PulsePage.css';

const { Title, Text, Paragraph } = Typography;

const PulseArticlePage: React.FC = () => {
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();
  const article = getPulseArticleBySlug(slug);

  if (!article) {
    return <Navigate to="/pulse" replace />;
  }

  const Body = article.body;

  return (
    <div className="pulse-page">
      <nav className="pulse-navbar">
        <div className="nav-container">
          <div className="nav-left">
            <div
              className="nav-logo"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  navigate('/');
                }
              }}
            >
              <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
            </div>
            <div className="nav-links">
              <a href="/platform">Platform</a>
              <a href="/markets">Markets</a>
              <a href="/pulse">Pulse</a>
              <a href="/press">Press</a>
            </div>
          </div>
          <div className="nav-actions">
            <Button type="primary" onClick={() => navigate('/login')}>Open Portal</Button>
          </div>
        </div>
      </nav>

      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/pulse')}
          className="back-to-home-button"
        >
          Back to Pulse
        </Button>
      </div>

      <div className="pulse-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <Card className="pulse-card">
              <article>
                <header className="article-detail-header">
                  <span className="article-detail-meta">{article.displayDate}</span>
                  <Title level={1} className="article-detail-title">
                    {article.title}
                  </Title>
                  <Paragraph className="article-detail-excerpt">{article.excerpt}</Paragraph>
                </header>
                <Divider className="article-detail-divider" />
                <div className="article-body">
                  <Suspense
                    fallback={
                      <div className="article-body-loading">
                        <Spin size="large" tip="Loading briefing…" />
                      </div>
                    }
                  >
                    <Body />
                  </Suspense>
                </div>
                <ArticleCta variant={article.ctaVariant ?? 'trial'} marketsHref={article.ctaMarketsHref} />
              </article>
            </Card>
          </Col>
        </Row>
      </div>

      <footer className="pulse-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PulseArticlePage;
